import { LoadingSpinner } from "@/modules/ui/components/loading"
import { useCallback, useEffect, useRef } from "react"

export type UseInfiniteScrollProps = {
  fetchNextPage: () => unknown
  hasNextPage: boolean
  isFetchingNextPage: boolean
}

export function useInfiniteScroll({ fetchNextPage, hasNextPage, isFetchingNextPage }: UseInfiniteScrollProps) {
  const loadMoreRef = useRef<HTMLDivElement | null>(null)

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const isIntersecting = entries[0]?.isIntersecting
      if (isIntersecting && hasNextPage && !isFetchingNextPage) {
        void fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  )

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection)

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current)
    }

    return () => observer.disconnect()
  }, [handleIntersection])

  const InfiniteScrollTrigger = useCallback(() => {
    if (!hasNextPage) return null
    return (
      <div ref={loadMoreRef} className="mt-2 flex w-full items-center justify-center py-3">
        {isFetchingNextPage && <LoadingSpinner className="h-6 w-6" />}
      </div>
    )
  }, [hasNextPage, isFetchingNextPage])

  return { InfiniteScrollTrigger }
}
